const DEFAULT_FLAGS = {
  socialSharingEnabled: true,
  publicFundShareEnabled: true,
  shareWhatsAppEnabled: true,
  shareInstagramEnabled: true,
  shareFacebookEnabled: true,
  shareEmailEnabled: true,
  shareImageEnabled: false,
};

const EMAIL_SUBJECT = 'Pfand-Beute bei Pfandpirat entdeckt';

function boolFlag(value, fallback = true) {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  return !['0', 'false', 'no', 'off'].includes(String(value).trim().toLowerCase());
}

export function getFundSharingFlags(runtime = {}) {
  return {
    socialSharingEnabled: boolFlag(runtime.socialSharingEnabled ?? runtime.SOCIAL_SHARING_ENABLED, DEFAULT_FLAGS.socialSharingEnabled),
    publicFundShareEnabled: boolFlag(runtime.publicFundShareEnabled ?? runtime.PUBLIC_FUND_SHARE_ENABLED, DEFAULT_FLAGS.publicFundShareEnabled),
    shareWhatsAppEnabled: boolFlag(runtime.shareWhatsAppEnabled ?? runtime.SHARE_WHATSAPP_ENABLED, DEFAULT_FLAGS.shareWhatsAppEnabled),
    shareInstagramEnabled: boolFlag(runtime.shareInstagramEnabled ?? runtime.SHARE_INSTAGRAM_ENABLED, DEFAULT_FLAGS.shareInstagramEnabled),
    shareFacebookEnabled: boolFlag(runtime.shareFacebookEnabled ?? runtime.SHARE_FACEBOOK_ENABLED, DEFAULT_FLAGS.shareFacebookEnabled),
    shareEmailEnabled: boolFlag(runtime.shareEmailEnabled ?? runtime.SHARE_EMAIL_ENABLED, DEFAULT_FLAGS.shareEmailEnabled),
    shareImageEnabled: boolFlag(runtime.shareImageEnabled ?? runtime.SHARE_IMAGE_ENABLED, DEFAULT_FLAGS.shareImageEnabled),
  };
}

export function sanitizePublicFundLocation(value) {
  const raw = String(value || '').trim();
  if (!raw) return 'ungefähre Umgebung';
  if (/[-+]?\d{1,3}[.,]\d{3,}/.test(raw) || /latitude|longitude|gps|koordinat/i.test(raw)) {
    return 'ungefähre Umgebung';
  }
  return raw.replace(/\s+/g, ' ').slice(0, 80);
}

export function estimateFundValueCents(input = {}) {
  const pfandType = String(input.pfand_type || input.pfandType || '').trim();
  const itemType = String(input.item_type || input.itemType || '').trim();
  const amountType = String(input.amount_type || input.amountType || '').trim();
  const explicitCents = Number(input.estimated_value_cents ?? input.estimatedValueCents ?? input.value_cents ?? input.valueCents);

  if (Number.isFinite(explicitCents) && explicitCents > 0) {
    return Math.round(explicitCents);
  }

  if (itemType === 'giveaway' || pfandType === 'zu_verschenken' || amountType === 'giveaway') {
    return 100;
  }

  if (pfandType === 'mehrweg' || amountType === 'mehrwegpfand') return 8;
  if (pfandType === 'einweg' || amountType === 'einwegpfand') return 25;
  if (pfandType === 'gemischt' || amountType === 'gemischtes_pfand') return 33;

  return 0;
}

export function formatEstimatedFundValue(input = {}) {
  const cents = typeof input === 'number' ? input : estimateFundValueCents(input);
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(cents || 0) / 100);
}

export function resolveFundTypeLabel(input = {}) {
  const label = String(input.pfand_type_label || input.pfandTypeLabel || input.amount_type_label || input.amountTypeLabel || '').trim();
  if (label) return label;

  const pfandType = String(input.pfand_type || input.pfandType || '').trim();
  const itemType = String(input.item_type || input.itemType || '').trim();
  const amountType = String(input.amount_type || input.amountType || '').trim();

  if (itemType === 'giveaway' || pfandType === 'zu_verschenken' || amountType === 'giveaway') return 'Zu verschenken';
  if (pfandType === 'mehrweg' || amountType === 'mehrwegpfand') return 'Mehrweg';
  if (pfandType === 'gemischt' || amountType === 'gemischtes_pfand') return 'Gemischtes Pfand';
  return 'Einweg';
}

export function buildFundShareUrl(input = {}, runtime = {}) {
  const explicitUrl = String(input.share_url || input.shareUrl || '').trim();
  if (explicitUrl && !/[?&](lat|lng|latitude|longitude)=/i.test(explicitUrl)) return explicitUrl;

  const base = String(runtime.appPublicBaseUrl || runtime.APP_PUBLIC_BASE_URL || runtime.appUrl || '/app/').trim() || '/app/';
  const normalizedBase = base.includes('#') ? base : base.replace(/\/?$/, '/');
  if (normalizedBase.includes('#')) return normalizedBase;
  return `${normalizedBase}#/map`;
}

export function buildFundShareText(input = {}, runtime = {}) {
  const value = formatEstimatedFundValue(input);
  const shareUrl = buildFundShareUrl(input, runtime);
  const typeLabel = resolveFundTypeLabel(input);
  const location = sanitizePublicFundLocation(input.public_location_label || input.location_label || input.locationLabel);
  const isGiveaway = String(input.item_type || input.itemType || '') === 'giveaway' || String(input.pfand_type || input.pfandType || '') === 'zu_verschenken';
  const valueLine = isGiveaway
    ? `Ich habe gerade einen Fund mit geschätztem Mindestwert von mindestens ${value} mit Pfandpirat gemeldet.`
    : `Ich habe gerade einen Fund im Wert von ca. ${value} mit Pfandpirat gemeldet.`;

  return [
    '🏴‍☠️ Pfand-Beute entdeckt!',
    valueLine,
    `Fundtyp: ${typeLabel}`,
    `Ort: ${location}`,
    'Wer ihn abholen will: Karte öffnen, Beute sichern, Stadt sauberer machen.',
    shareUrl,
    '',
    '#Pfandpirat #MachAusMüllBeute',
  ].join('\n');
}

export function buildFundSharePayload(input = {}, runtime = {}) {
  const url = buildFundShareUrl(input, runtime);
  const text = buildFundShareText(input, runtime);
  return {
    title: 'Pfand-Beute entdeckt!',
    text,
    url,
    fundType: resolveFundTypeLabel(input),
    estimatedValue: formatEstimatedFundValue(input),
    locationLabel: sanitizePublicFundLocation(input.public_location_label || input.location_label || input.locationLabel),
  };
}

export function buildWhatsAppShareUrl(payload) {
  return `https://wa.me/?text=${encodeURIComponent(payload.text)}`;
}

export function buildFacebookShareUrl(payload) {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(payload.url)}`;
}

export function buildEmailShareUrl(payload) {
  const body = `${payload.text}\n\nHinweis: Es wird keine exakte Adresse öffentlich geteilt.`;
  return `mailto:?subject=${encodeURIComponent(EMAIL_SUBJECT)}&body=${encodeURIComponent(body)}`;
}

export async function copyShareLink(payload) {
  const text = payload?.text || String(payload?.url || '');
  if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return { ok: true, mode: 'clipboard' };
  }
  return { ok: false, mode: 'manual', message: text };
}

async function nativeShare(payload) {
  if (typeof navigator === 'undefined' || !navigator?.share) return false;
  await navigator.share({ title: payload.title, text: payload.text, url: payload.url });
  return true;
}

function openShareWindow(url) {
  if (typeof window === 'undefined') return;
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    window.location.href = url;
  }
}

export async function shareToWhatsApp(payload) {
  if (await nativeShare(payload)) return { ok: true, mode: 'native' };
  openShareWindow(buildWhatsAppShareUrl(payload));
  return { ok: true, mode: 'link' };
}

export async function shareToInstagram(payload) {
  if (await nativeShare(payload)) return { ok: true, mode: 'native' };
  await copyShareLink(payload);
  return {
    ok: false,
    mode: 'manual',
    message: 'Instagram lässt sich hier nicht direkt öffnen. Kopiere den Text oder teile das Bild über dein Handy-Menü.',
  };
}

export async function shareToFacebook(payload) {
  if (await nativeShare(payload)) return { ok: true, mode: 'native' };
  try {
    await copyShareLink(payload);
  } catch (error) {
    // Facebook gets the URL; copied text is a helpful extra when available.
  }
  openShareWindow(buildFacebookShareUrl(payload));
  return { ok: true, mode: 'link' };
}

export async function shareByEmail(payload) {
  if (typeof window === 'undefined') return { ok: false, mode: 'manual' };
  window.location.href = buildEmailShareUrl(payload);
  return { ok: true, mode: 'mailto' };
}

export async function shareFund(channel, payload) {
  switch (channel) {
    case 'whatsapp':
      return shareToWhatsApp(payload);
    case 'instagram':
      return shareToInstagram(payload);
    case 'facebook':
      return shareToFacebook(payload);
    case 'email':
      return shareByEmail(payload);
    case 'copy':
      return copyShareLink(payload);
    default:
      return nativeShare(payload);
  }
}
